import { useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";

import {
  SettingDefinitionEdit as ISettingDefinitionEdit,
  SettingDefinitionEditConstructor,
} from "../../models/SettingDefinitionEdit";

import EntitySelectForm from "../form/EntitySelectForm";
import SettingTypeSelectForm from "../form/SettingTypeSelectForm";
import FormCardWrapper from "../../../../components/reusable-components/form/FormCardWrapper";
import FormTextField from "../../../../components/reusable-components/form/MuiFormInput";
import SubmitButton from "../../../../components/reusable-components/form/SubmitButton";

import { useFieldRules } from "../../../../hooks/useFieldRules";
import { useApiFieldErrors } from "../../../../hooks/fetch/useApiFieldErrors";
import { useRefetchOnSuccess } from "../../../../hooks/fetch/useRefetchOnSuccess";
import { useEditSettingDefinition } from "../../hooks/useEditSettingDefinition";
import { useSettingDefinition } from "../../hooks/useFetchSettingDefinition";

const SettingDefinitionEdit = () => {
  const { id } = useParams<{ id: string }>();
  const rules = useFieldRules();

  const {
    data: settingDefinition,
    fetch: { loading, refetch, error: fetchError },
  } = useSettingDefinition({
    id: id!,
  });

  const {
    fetch: {
      fetchData: editSettingDefinition,
      error: editError,
      success: editSuccess,
      loading: editLoading,
    },
  } = useEditSettingDefinition({
    id: id!,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    setError,
    clearErrors,
    reset,
    control,
  } = useForm<SettingDefinitionEditConstructor>({
    defaultValues: {
      entity_type: undefined,
      key: "",
      type: undefined,
      default_value: "",
      description: "",
    },
    mode: "all",
  });

  useEffect(() => {
    if (settingDefinition) {
      reset({
        entity_type: {
          label: settingDefinition.entity_type,
          value: settingDefinition.entity_type,
        },
        key: settingDefinition.key,
        type: {
          label: settingDefinition.type,
          value: settingDefinition.type,
        },
        default_value: settingDefinition.default_value,
        description: settingDefinition.description,
      });
    }
  }, [settingDefinition, reset]);

  useApiFieldErrors(editError, setError);
  useRefetchOnSuccess(editSuccess, refetch);

  const onSubmit: SubmitHandler<SettingDefinitionEditConstructor> = async (
    formData
  ) => {
    clearErrors();
    await editSettingDefinition({
      body: new ISettingDefinitionEdit(formData),
    });
  };

  const isLoading = loading || editLoading || isSubmitting;

  return (
    <FormCardWrapper
      title="Edit Setting Definition"
      successText="Setting Definitioneditd successfully!"
      success={editSuccess}
      error={fetchError || editError}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <EntitySelectForm
            name="entity_type"
            control={control}
            disabled={isLoading}
            controllerProps={{
              rules: rules("Entity Type").required().get(),
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormTextField
            name="key"
            label="Key"
            control={control}
            textFieldProps={{ disabled: isLoading }}
            rules={rules("Key").required().maxLength(100).get()}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SettingTypeSelectForm
            name="type"
            control={control}
            disabled={isLoading}
            controllerProps={{
              rules: rules("Type").required().get(),
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormTextField
            name="default_value"
            label="Default Value"
            control={control}
            textFieldProps={{ disabled: isLoading }}
            rules={rules("Default Value").maxLength(255).get()}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <FormTextField
            name="description"
            label="Description"
            control={control}
            textFieldProps={{ disabled: isLoading }}
            rules={rules("Description").required().maxLength(500).get()}
          />
        </Grid>
      </Grid>

      <SubmitButton
        text="Update Setting Definition"
        loading={editLoading || isSubmitting}
        disabled={isLoading}
      />
    </FormCardWrapper>
  );
};

export default SettingDefinitionEdit;
